import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CircleAlert, Loader2 } from 'lucide-react'
import { Button } from '@/src/renderer/components/ui/Button'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { useAccountSource } from '@/src/renderer/lib/accountSource'
import { useBackupBridge } from '@/src/renderer/lib/backup/backupBridge'
import { backupFileName, packForDevice } from '@/src/renderer/lib/backup/packBackup'
import { allSelectableKeys, buildSelectionTree, carrySelection, selectRows, selectsEverything } from '@/src/renderer/lib/backup/selection'
import type { BackupSnapshot } from '@/src/renderer/lib/backup/format'
import { SettingsSubpage } from '../../primitives'
import { BackupSelectionTree } from './BackupSelectionTree'
import './BackupViews.scss'

/** Der Baum ohne „Einstellungen“ — die gehen beim Erstellen immer mit (Entscheidung 2a). */
const contentTree = (snapshot: BackupSnapshot) => buildSelectionTree(snapshot.tables).filter(node => node.kind !== 'settings')

interface CreateBackupViewProps {
  onBack: () => void
  /** Gespeichert — die Übersicht meldet es (das Einstellungsfenster hat keine Toasts). */
  onCreated: (fileName: string) => void
}

/**
 * **Backups › Backup erstellen** (#158): Auswahl → „Backup erstellen …“ → Speichern-Dialog →
 * zurück zur Übersicht, die den Erfolg meldet. Einstellungen gehen immer mit (Entscheidung 2a), das
 * Profil als Auskunft auch (Entscheidung 7). Mit Passwort geschützt, wenn eins gesetzt ist (Entscheidung 17).
 */
export function CreateBackupView({ onBack, onCreated }: CreateBackupViewProps) {
  const { t } = useTranslation()
  const dataSource = useDataSource()
  const account = useAccountSource()
  const bridge = useBackupBridge()
  const [source, setSource] = useState<{ snapshot: BackupSnapshot; accountId: string | null } | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  // Schaltet Menü oder Doppelklick während des Speichern-Dialogs um, darf das fertige Backup die neue Unterseite nicht schließen.
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const tree = useMemo(() => (source ? contentTree(source.snapshot) : []), [source])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const accountId = await account.getUserId().catch(() => null)
      const snapshot = await dataSource.readBackupRows({ accountId })
      if (cancelled) return
      setSource({ snapshot, accountId })
      setSelected(allSelectableKeys(contentTree(snapshot)))
    })()
    return () => {
      cancelled = true
    }
  }, [account, dataSource])

  const onCreate = async () => {
    if (!source || busy) return
    setBusy(true)
    setFailed(false)
    try {
      // Frisch lesen: Die Unterseite kann lange offen gestanden haben, das Hauptfenster schreibt weiter.
      const snapshot = await dataSource.readBackupRows({ accountId: source.accountId })
      const freshTree = contentTree(snapshot)
      const chosen = carrySelection(tree, selected, freshTree)
      const tables = selectRows(snapshot.tables, new Set([...chosen, 'settings']), { allTags: selectsEverything(freshTree, chosen) })
      const bytes = await packForDevice({ accountId: source.accountId, profile: snapshot.profile, tables }, bridge)
      const result = await bridge.saveAs(bytes, backupFileName(), 'backup')
      if (result.saved && mounted.current) onCreated(result.name ?? backupFileName())
    } catch (error) {
      console.error('[Backup] Erstellen fehlgeschlagen:', error)
      setFailed(true)
    } finally {
      setBusy(false)
    }
  }

  const nothingChosen = selected.size === 0

  return (
    <SettingsSubpage pageTitle={t('settings.subNav.backups')} title={t('preferences.backups.createTitle')} onBack={onBack} locked={busy}>
      <p className="backup-views__intro">{t('preferences.backups.createDescription')}</p>
      {source ? (
        <>
          <BackupSelectionTree tree={tree} value={selected} onValueChange={setSelected} disabled={busy} />
          <p className="backup-views__hint">{t('preferences.backups.settingsAlways')}</p>
          <div className="backup-views__actions">
            <Button
              variant="primary"
              disabled={busy || nothingChosen}
              icon={busy ? <Loader2 size={16} className="backup-views__spinner" aria-hidden="true" /> : undefined}
              onClick={() => void onCreate()}
            >
              {t('preferences.backups.create')}
            </Button>
          </div>
          {failed && (
            <p className="backup-views__result backup-views__result--failed" role="alert">
              <CircleAlert size={16} aria-hidden="true" />
              {t('preferences.backups.createFailed')}
            </p>
          )}
        </>
      ) : (
        <p className="backup-views__loading">
          <Loader2 size={16} className="backup-views__spinner" aria-hidden="true" />
          {t('preferences.backups.loading')}
        </p>
      )}
    </SettingsSubpage>
  )
}
